/**
 * Type definitions for Step3Requirements component and its sub-components
 * Extracted from main component for better organization and reusability
 */

import React from 'react';
import { UseFormReturnType } from '@mantine/form';
import { MantineWizardFormData } from '../../utils/mantineFormSchema';
import { InlineRequirement } from '../../types';
import { Task } from '../../../../../types';

/**
 * Props for the main Step3Requirements component
 */
export interface Step3RequirementsProps {
  /** Mantine form instance for wizard data */
  form: UseFormReturnType<MantineWizardFormData>;
  /** Whether the component is in loading state */
  isLoading?: boolean;
  /** Task being edited (for edit mode) */
  editingTask?: Task | null;
  /** Edit session identifier */
  editSessionId?: string | null;
}

/**
 * Props for RequirementsHeader component
 */
export interface RequirementsHeaderProps {
  /** Translation function */
  t: (key: string) => string;
  /** Total count of requirements */
  totalCount: number;
}

/**
 * Props for RequirementInput component
 */
export interface RequirementInputProps {
  /** Current input value */
  value: string;
  /** Callback when input value changes */
  onChange: (value: string) => void;
  /** Callback when requirement should be added */
  onAdd: () => void;
  /** Callback for key press events */
  onKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Whether this is a mobile view */
  isMobile?: boolean;
  /** Translation function */
  t: (key: string) => string;
  /** Autocomplete data for suggestions */
  autocompleteData: AutocompleteOptionData[];
  /** Custom render function for suggestion options */
  renderOption: (props: { option: AutocompleteOptionData }) => React.ReactElement;
}

/**
 * Props for RequirementsList component
 */
export interface RequirementsListProps {
  /** Array of requirements to display */
  requirements: (Task['requirements'][0] | InlineRequirement)[];
  /** Callback when drag and drop reordering occurs */
  onDragEnd: (result: { destination: { index: number } | null; source: { index: number } | null }) => void;
  /** Callback when a requirement is updated */
  onUpdate: (index: number, text: string) => void;
  /** Callback when a requirement is removed */
  onRemove: (index: number) => void;
  /** Function to get stable key for requirement */
  getRequirementKey: (requirement: Task['requirements'][0] | InlineRequirement) => string;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Translation function */
  t: (key: string) => string;
  /** Form errors object */
  formErrors?: { requirements?: string };
}

/**
 * Props for RequirementItem component
 */
export interface RequirementItemProps {
  /** The requirement data */
  requirement: Task['requirements'][0] | InlineRequirement;
  /** Index of the requirement in the list */
  index: number;
  /** Callback when requirement text is updated */
  onUpdate: (index: number, text: string) => void;
  /** Callback when requirement is removed */
  onRemove: (index: number) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Translation function */
  t: (key: string) => string;
}

/**
 * Props for RequirementSuggestions component (if needed for separate suggestions display)
 */
export interface RequirementSuggestionsProps {
  /** Available suggestion categories */
  suggestions: RequirementSuggestion[];
  /** Callback when a suggestion is selected */
  onSelect: (suggestion: RequirementSuggestion) => void;
  /** Translation function */
  t: (key: string) => string;
  /** Whether suggestions are visible */
  visible?: boolean;
}

/**
 * Structure for requirement suggestions
 */
export interface RequirementSuggestion {
  /** The suggestion text/value */
  value: string;
  /** Category this suggestion belongs to */
  category: string;
  /** Icon component for visual representation */
  icon: React.ComponentType<{
    size?: number;
    stroke?: number;
    color?: string;
    className?: string;
  }>;
}

/**
 * Autocomplete option data structure
 */
export interface AutocompleteOptionData {
  /** Option value */
  value: string;
  /** Option label (usually same as value) */
  label?: string;
  /** Category for grouping */
  category?: string;
  /** Icon component */
  icon?: React.ComponentType<{
    size?: number;
    stroke?: number;
    color?: string;
    className?: string;
  }>;
}

/**
 * Configuration for requirement categories and their visual representation
 */
export interface RequirementCategoryConfig {
  /** Category identifier key */
  key: string;
  /** Color value for the category */
  color: string;
  /** Icon component for the category */
  icon: React.ComponentType<{
    size?: number;
    stroke?: number;
    color?: string;
    className?: string;
  }>;
}

/**
 * Type guard to check if a requirement is an InlineRequirement
 */
export const isInlineRequirement = (
  req: Task['requirements'][0] | InlineRequirement
): req is InlineRequirement => {
  return 'tempId' in req;
};

/**
 * Helper function type for getting stable requirement keys
 */
export type GetRequirementKeyFunction = (
  requirement: Task['requirements'][0] | InlineRequirement
) => string;

/**
 * Drag and drop result structure
 */
export interface DragDropResult {
  /** Destination information */
  destination: { index: number } | null;
  /** Source information */
  source: { index: number } | null;
}

/**
 * Category colors mapping type
 */
export type CategoryColors = Record<string, string>;